import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { LifecycleNodeKind, useLinkedNodes } from '../../../components/LinkedChips';
import { useView } from '../../../lib/access/useView';
import { projectDataClient } from '../../../lib/connect/clients';
import { useTenantId } from '../../Delivery/_shared/hooks/useTenantId';
import { PURSUIT_HEADER_KEY, PURSUIT_THREAD_KEY } from './pursuitQueryKeys';
import { ThreadGroup, toPursuitHeader } from './pursuitReadTypes';
/**
 * The pursuit header read model (GRW-12 `GetPursuitHeader`): name, labels,
 * capabilities (server truth), KPI sources and the facts rail in one call.
 * Mutations that change a header fact call `invalidatePursuitReads`
 * (pursuitQueryKeys.ts). Pass '' to keep it idle (flag off).
 */ export function usePursuitHeader(captureProjectId) {
    const tenantId = useTenantId();
    const query = useQuery({
        queryKey: [
            PURSUIT_HEADER_KEY,
            tenantId,
            captureProjectId
        ],
        queryFn: async ()=>toPursuitHeader(await projectDataClient.getPursuitHeader({
                captureProjectId
            })),
        staleTime: 30000,
        enabled: !!captureProjectId
    });
    return {
        data: query.data,
        isLoading: query.isLoading,
        error: query.error
    };
}
/**
 * Outputs / win themes / black hat write gate: capture update AND project
 * write, exactly as OutputsWorkspace computes it.
 */ export function usePursuitOutputsWrite(captureProjectId, zouProjectId) {
    const ref = {
        id: captureProjectId,
        objectType: 'delivery.programs',
        objectId: zouProjectId
    };
    const edit = useView({
        kind: 'capture_project',
        ...ref
    }, {
        action: 'update'
    });
    const write = useView({
        kind: 'project_write',
        ...ref
    }, {
        action: 'project'
    });
    return edit.can('update') && write.can('project');
}
/**
 * Proposals spawned from this pursuit. There is no ListCaptureProposalLinks
 * RPC on main, so this reads the proposal edges of GetLinkedNodes (readable
 * neighbours only), the same source as LinkedChips.
 */ export function useCaptureProposalLinks(captureProjectId) {
    const linked = useLinkedNodes(LifecycleNodeKind.PURSUIT, captureProjectId);
    const proposals = useMemo(()=>(linked.data?.edges ?? []).map((edge)=>edge.node).filter((node)=>node?.kind === LifecycleNodeKind.PROPOSAL).map((node)=>({
                id: node?.id ?? '',
                zouProjectId: node?.zouProjectId || node?.id || '',
                label: node?.label?.trim() || 'Proposal'
            })), [
        linked.data?.edges
    ]);
    return {
        proposals,
        isLoading: linked.isLoading
    };
}
export const THREAD_FILTER_GROUPS = {
    all: [],
    market: [
        ThreadGroup.MARKET,
        ThreadGroup.WORKSPACES
    ],
    customer: [
        ThreadGroup.CUSTOMER
    ],
    shaping: [
        ThreadGroup.SHAPING
    ],
    decisions: [
        ThreadGroup.DECISIONS
    ]
};
/**
 * The pursuit thread (GRW-13 `ListThread`), 50 events a page, newest first.
 * Invalidated together with the header (`invalidatePursuitReads`).
 */ export function useThread(captureProjectId, filter) {
    const tenantId = useTenantId();
    return useInfiniteQuery({
        queryKey: [
            PURSUIT_THREAD_KEY,
            tenantId,
            captureProjectId,
            filter
        ],
        queryFn: ({ pageParam })=>projectDataClient.listThread({
                captureProjectId,
                groups: THREAD_FILTER_GROUPS[filter],
                pageSize: 50,
                pageToken: pageParam
            }),
        initialPageParam: '',
        getNextPageParam: (page)=>page.nextPageToken || undefined,
        enabled: !!captureProjectId
    });
}
